import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { Envelope, Eye, Fonts, Invitation, Link, MailingAddress, Mobile, Palette, Share, Website } from '@withjoy/joykit/icons';

export type DigitalValuePropItem = Readonly<{
  key: string;
  icon: JSX.Element;
  title: string;
  description: string;
}>;

export type DigitalValuePropSection = Readonly<{
  key: 'included' | 'premiumAdditions' | 'purchaseOptions';
  title: string;
  items: ReadonlyArray<DigitalValuePropItem>;
}>;

const icon = (Icon: typeof Website) => (
  <Icon
    size={24}
    alignSelf="flex-start"
  />
);

/**
 * The value props shared by the Premium Digital and Basic Digital V2 PDPs. Both tabs
 * render the same list so the tiers read as one comparison. The copy is still
 * settling, so every bullet lives here -- edit this array, not the panes.
 */
export const useDigitalValuePropSections = (): ReadonlyArray<DigitalValuePropSection> => {
  const { tq } = useTranslation('stationery');

  return useMemo(
    () => [
      {
        key: 'included',
        title: tq`Digital Cards include:`,
        items: [
          {
            key: 'designs',
            icon: icon(Website),
            title: tq`Designs`,
            description: tq`Beautiful, unique templates to pick from, designed to match your wedding website.`,
          },
          {
            key: 'links',
            icon: icon(Link),
            title: tq`Links`,
            description: tq`Link to your wedding website, RSVP, Add to Calendar, Registry, and more.`,
          },
          {
            key: 'contactCollector',
            icon: icon(MailingAddress),
            title: tq`Contact Collector`,
            description: tq`Magically collect guests' contact info in your guest list for sending cards and thank you notes.`,
          },
          {
            key: 'shareable',
            icon: icon(Share),
            title: tq`Easily Shareable`,
            description: tq`Get a link to your digital card that you can share directly with your guests.`,
          },
          {
            key: 'tracking',
            icon: icon(Eye),
            title: tq`Tracking`,
            description: tq`Easily track which guests have received and opened your digital card.`,
          },
        ],
      },
      {
        key: 'premiumAdditions',
        title: tq`Premium Digital Cards additionally include:`,
        items: [
          {
            key: 'premiumCustomization',
            icon: icon(Fonts),
            title: tq`Premium Customization`,
            description: tq`Change fonts, move text around, upload your own image, and more.`,
          },
          {
            key: 'cardBack',
            icon: icon(Palette),
            title: tq`Card Back Design`,
            description: tq`Choose from a variety of card back designs or upload your own. Color match the front of your card.`,
          },
          {
            key: 'premiumEnvelope',
            icon: icon(Envelope),
            title: tq`Premium Envelope`,
            description: tq`Choose envelope colors and animations. Color match the front of your card.`,
          },
        ],
      },
      {
        key: 'purchaseOptions',
        title: tq`Options for purchase:`,
        items: [
          {
            key: 'textMessage',
            icon: icon(Mobile),
            title: tq`Text Message Delivery`,
            description: tq`Text messaging to USA and Canada with recipient tracking.`,
          },
          {
            key: 'matchingPaper',
            icon: icon(Invitation),
            title: tq`Matching Paper Cards`,
            description: tq`Order printed cards for special guests or keepsakes.`,
          },
        ],
      },
    ],
    [tq],
  );
};
